export const categories = [
  {
    id: 'plot-analysis',
    name: '剧情解析',
    description: '深度剖析夜勤病栋的故事脉络与叙事结构',
    icon: 'ri-file-search-line',
    color: 'primary',
    articleCount: 12,
  },
  {
    id: 'horror-elements',
    name: '恐怖元素',
    description: '解析作品中令人不寒而栗的恐怖手法',
    icon: 'ri-ghost-line',
    color: 'accent',
    articleCount: 8,
  },
  {
    id: 'characters',
    name: '人物介绍',
    description: '深入了解每个角色的背景与命运',
    icon: 'ri-user-settings-line',
    color: 'secondary',
    articleCount: 10,
  },
  {
    id: 'classic-scenes',
    name: '经典场景',
    description: '回顾那些让人记忆犹新的经典画面',
    icon: 'ri-film-line',
    color: 'primary',
    articleCount: 15,
  },
  {
    id: 'cultural-impact',
    name: '文化影响',
    description: '探讨作品对流行文化与社会的影响',
    icon: 'ri-global-line',
    color: 'accent',
    articleCount: 6,
  },
  {
    id: 'viewing-guide',
    name: '观看指南',
    description: '新老观众皆宜的观影与阅读指南',
    icon: 'ri-guide-line',
    color: 'secondary',
    articleCount: 5,
  },
];

export interface Article {
  id: string;
  slug: string;
  title: string;
  category: string;
  excerpt: string;
  content: string;
  image: string;
  imageWidth: number;
  imageHeight: number;
  date: string;
  author: string;
  readTime: number;
  featured: boolean;
  tags: string[];
}

export const articles: Article[] = [
  {
    id: 'a1',
    slug: 'yeqin-bingdong-quanjiexi',
    title: '夜勤病栋全解析：从第一夜到最终夜的恐怖之旅',
    category: 'plot-analysis',
    excerpt: '从第一夜的诡异氛围到最终夜的惊天反转，全面梳理夜勤病栋的完整剧情脉络，带你理解每一个关键情节节点背后的深意。',
    content: `<p>《夜勤病栋》作为恐怖题材的经典之作，其叙事结构精妙绝伦。故事以一家名为"圣玛丽亚"的医院为背景，讲述了一系列发生在深夜病房中的离奇事件。</p>
<h2>第一夜：黑暗降临</h2>
<p>故事的开端看似平静。新入职的夜班护士在例行查房时，开始注意到一些不寻常的现象。病房走廊尽头的灯光忽明忽暗，空无一人的病房传来轻微的脚步声，以及病人神秘消失又突然出现的诡异事件。</p>
<p>这一阶段，作品巧妙运用了"渐进式恐惧"的手法——从日常的违和感开始，逐渐将观众引入一个充满未知与恐惧的世界。导演通过细腻的镜头语言，将医院固有的冰冷与疏离感放大到了极致。</p>
<h2>第二至四夜：恐惧升级</h2>
<p>随着剧情推进，夜班护士们开始遭遇更加直接的超自然现象。深夜时分的婴儿啼哭声、镜中映出的陌生人影、以及病历上凭空出现的陌生名字，每一个细节都在暗示着一场更大的阴谋。</p>
<p>这一阶段最令人印象深刻的是"走廊追逐"场景：长达三分钟的一镜到底拍摄，护士在无尽的走廊中奔跑，身后的脚步声逐渐逼近，却始终看不到任何追赶者。这种看不见的恐惧远胜于任何具象的怪物。</p>
<h2>第五至七夜：真相浮现</h2>
<p>故事进入高潮阶段。通过一系列闪回与线索拼凑，观众逐渐了解到医院背后隐藏的黑暗历史——三十年前发生在这里的一场医疗事故，以及由此引发的一系列悲剧。</p>
<p>当年一位年仅七岁的患者因医疗失误而去世，他的母亲在绝望中选择了在同一个病房结束生命。自此之后，每到深夜，医院便会成为两个世界交汇的场所。</p>
<h2>最终夜：惊天反转</h2>
<p>在最终夜中，主角发现了一个令人震惊的真相：她自己就是当年那位母亲灵魂的一部分转世。整个故事实际上是一场自我救赎的旅程。这个反转不仅解开了之前所有谜团，更赋予了整个故事深刻的哲学意味——关于生命、死亡、以及灵魂的归宿。</p>`,
    image: 'https://readdy.ai/api/search-image?query=Dark%20hospital%20corridor%20at%20night%20with%20dim%20flickering%20fluorescent%20lights%2C%20long%20empty%20hallway%20with%20eerie%20atmosphere%2C%20abandoned%20medical%20equipment%20scattered%2C%20cold%20clinical%20aesthetic%20with%20deep%20shadows%2C%20cinematic%20horror%20lighting%2C%20foggy%20atmosphere%2C%20dark%20moody%20photography%20style%2C%20no%20people%2C%20high%20contrast%20between%20light%20and%20shadow&width=1600&height=900&seq=article-a1-hero&orientation=landscape',
    imageWidth: 1600,
    imageHeight: 900,
    date: '2026-08-10',
    author: '暗夜解析人',
    readTime: 12,
    featured: true,
    tags: ['夜勤病栋', '剧情解析', '全解析', '深度分析'],
  },
  {
    id: 'a2',
    slug: 'kongbu-yuansu-shendu-pouxi',
    title: '夜勤病栋的恐怖元素深度剖析：为什么它会让你彻夜难眠',
    category: 'horror-elements',
    excerpt: '从心理学角度分析夜勤病栋中的核心恐怖元素，揭示这部作品为什么能精准击中人类最深层的恐惧。',
    content: `<p>《夜勤病栋》之所以能成为经典，与其精妙的恐怖元素运用密不可分。本文将从心理学和电影语言两个角度，深度剖析这部作品中的恐怖机制。</p>
<h2>环境恐怖：医院作为恐惧容器</h2>
<p>医院本身就是一个充满矛盾的空间——既是救死扶伤的圣地，也是死亡与病痛的集中营。作品巧妙地利用了人们对医院的本能恐惧：消毒水的气味、冰冷的医疗器械、单调的白墙与走廊，以及深夜时分特有的孤寂感。</p>
<p>尤其是"深夜"这个时间设定。研究表明，凌晨两点到四点是人类生理节律的最低点，也是心理防御最薄弱的时刻。作品将所有恐怖事件设置在这个时段，精准地击中了观众的心理弱点。</p>
<h2>未知恐惧：看不见的东西最可怕</h2>
<p>作品大量运用了"留白"手法。很多恐怖场景中，观众只知道"有东西在那里"，却看不清它的真面目。这种不确定性激发了人类大脑中最原始的恐惧机制——对未知的恐惧。</p>
<p>经典的"门缝视角"镜头：摄像机从门缝中窥视病房内部，观众能看到病床上隆起的被子在缓缓起伏，却不知道被子下面究竟是什么。这种克制的展现方式比任何直白的恐怖画面都更加令人不安。</p>
<h2>声音设计：听觉恐怖的极致</h2>
<p>作品的音效设计堪称教科书级别。婴儿的啼哭声、轮椅的金属摩擦声、呼吸机的机械节律、以及最重要的——寂静。在某些关键场景中，完全的静默反而成为了最令人窒息的恐怖元素。</p>
<p>低频音效的运用尤为出色。人耳虽然听不到极低频率的声音，但身体能够感受到振动。作品在多个场景中叠加了次声波频段的环境音，让观众在不知不觉中产生生理性的不安。这是真正意义上的"用身体感受恐惧"。</p>`,
    image: 'https://readdy.ai/api/search-image?query=Dark%20empty%20hospital%20room%20at%20midnight%20with%20a%20single%20bed%2C%20moonlight%20streaming%20through%20blinds%20creating%20striped%20shadows%2C%20eerie%20cold%20atmosphere%2C%20medical%20IV%20stand%20silhouette%2C%20abandoned%20wheelchair%20in%20corner%2C%20cinematic%20horror%20photography%2C%20dark%20moody%20ambiance%2C%20soft%20fog%2C%20high%20contrast&width=1600&height=900&seq=article-a2-hero&orientation=landscape',
    imageWidth: 1600,
    imageHeight: 900,
    date: '2026-08-08',
    author: '暗夜解析人',
    readTime: 10,
    featured: true,
    tags: ['夜勤病栋', '恐怖元素', '心理学', '音效设计'],
  },
  {
    id: 'a3',
    slug: 'zhuyao-renwu-jieshao',
    title: '夜勤病栋主要人物介绍：每个角色背后的故事',
    category: 'characters',
    excerpt: '详细介绍夜勤病栋中的每一个关键人物，从主角到配角，了解他们的性格、命运与在故事中的作用。',
    content: `<p>《夜勤病栋》的人物塑造堪称一绝。每一个角色都有着完整的背景故事和清晰的心理动机，这使得整部作品在恐怖之外还具备了深厚的人文关怀。</p>
<h2>主角：佐藤美咲（夜班护士）</h2>
<p>作为故事的引导者，美咲是一个性格温柔但内心坚强的年轻护士。她在圣玛丽亚医院值夜班的经历构成了故事的主线。美咲的特殊之处在于她对超自然现象有着超乎寻常的感知能力——这实际上是故事最大的伏笔之一。</p>
<p>她的人物弧光十分完整：从最初的不安与恐惧，到中期的挣扎与探索，再到最后的觉醒与救赎。演员通过细腻的表情和肢体语言，将这种逐渐转变的心理状态表现得淋漓尽致。</p>
<h2>神秘人物：白衣少年</h2>
<p>白衣少年是作品中最为诡谲的存在。他总是出现在最不可能出现的地方——走廊尽头、楼梯转角、镜面反射中。他从不说话，只是静静地注视着主角和观众。</p>
<p>随着剧情的推进，观众会逐渐意识到，白衣少年并非传统意义上的"鬼魂"，而是一种更为复杂的存在——他是三十年前那场悲剧的具象化，是未了心愿的物质形态。</p>
<h2>配角群像</h2>
<p>作品中还有多位令人印象深刻的配角：经验丰富却对超自然事件讳莫如深的护士长、总是能在关键时刻提供线索的医院老保安、以及那位看似疯癫实则洞察一切的年长病人。每一个人物都在推动剧情发展中发挥着不可或缺的作用。</p>`,
    image: 'https://readdy.ai/api/search-image?query=Silhouette%20of%20a%20young%20nurse%20in%20uniform%20standing%20at%20the%20end%20of%20a%20dark%20hospital%20corridor%2C%20backlit%20by%20a%20single%20flickering%20overhead%20light%2C%20eerie%20atmosphere%2C%20cinematic%20composition%2C%20dark%20dramatic%20lighting%2C%20mysterious%20mood%2C%20no%20gore%2C%20artistic%20horror%20photography&width=1600&height=900&seq=article-a3-hero&orientation=landscape',
    imageWidth: 1600,
    imageHeight: 900,
    date: '2026-08-05',
    author: '暗夜解析人',
    readTime: 8,
    featured: false,
    tags: ['夜勤病栋', '人物介绍', '角色分析'],
  },
  {
    id: 'a4',
    slug: 'jingdian-changjing-huigu',
    title: '十大经典场景回顾：夜勤病栋中最令人难忘的瞬间',
    category: 'classic-scenes',
    excerpt: '精选夜勤病栋中十个最令人难以忘怀的经典场景，回顾那些让人心跳加速、毛骨悚然的时刻。',
    content: `<p>《夜勤病栋》中有太多令人难忘的场景，我们从中挑选了十个最具代表性和冲击力的经典瞬间进行回顾。</p>
<h2>场景一：走廊尽头的呼唤</h2>
<p>这是整个故事中第一个真正意义上的恐怖场景。深夜三点，美咲在巡房时听到走廊尽头传来微弱的呼唤声——呼唤的正是她的名字。当她鼓起勇气走近时，声音却骤然消失，只剩下走廊灯管发出的嗡嗡声。</p>
<p>这个场景的恐怖之处在于它的克制。没有任何跳跃惊吓（jump scare），只有声音带来的心理暗示。观众和美咲一起，在那一刻感受到了真正的恐惧。</p>
<h2>场景二：镜中的陌生人</h2>
<p>美咲在洗手间洗手时，抬头看到镜子中自己的倒影身后站着一个白衣少年。她猛地回头——身后空无一人。再看向镜子时，少年已经消失了。这个场景运用了经典的"镜子恐怖"手法，效果极佳。</p>
<h2>场景三：空病房的呼吸声</h2>
<p>304号病房已经没有病人入住，但每到凌晨，路过的人都能听到病房内传来均匀的呼吸声。美咲最终进入病房查看——病床上空无一人，但被子却微微隆起，像是有人正睡在上面。</p>
<p>这个场景的拍摄手法极为精妙：摄像机缓慢推进，观众的心跳随着镜头的靠近而加速。最终镜头停在离被子只有几厘米的距离，观众几乎能感受到那种令人窒息的紧张感。</p>`,
    image: 'https://readdy.ai/api/search-image?query=Dark%20hospital%20bathroom%20with%20a%20nurse%20looking%20into%20a%20foggy%20mirror%2C%20mysterious%20figure%20reflected%20behind%20her%2C%20dramatic%20cinematic%20lighting%2C%20cold%20blueish%20tone%2C%20horror%20film%20aesthetic%2C%20eerie%20mood%2C%20steam%20from%20sink%2C%20dim%20flickering%20lights&width=1600&height=900&seq=article-a4-hero&orientation=landscape',
    imageWidth: 1600,
    imageHeight: 900,
    date: '2026-08-03',
    author: '暗夜解析人',
    readTime: 14,
    featured: true,
    tags: ['夜勤病栋', '经典场景', '回顾', '十大'],
  },
  {
    id: 'a5',
    slug: 'wenhua-yingxiang-pinggu',
    title: '夜勤病栋的文化影响：从亚文化现象到主流认可',
    category: 'cultural-impact',
    excerpt: '探讨夜勤病栋如何从一个小众恐怖作品逐步演变为具有广泛影响力的文化现象，以及它对后续创作的影响。',
    content: `<p>《夜勤病栋》在恐怖题材领域的地位，已经远远超出了一部普通作品能够达到的高度。它已经成为一个文化符号，影响着全球范围内的恐怖创作。</p>
<h2>从地下到主流</h2>
<p>作品最初只在恐怖爱好者的小圈子中流传。然而随着口碑发酵，它逐渐突破了圈层限制，吸引了大量普通观众的关注。这种"由下而上"的传播路径，使其保持了最初的艺术纯粹性，没有被商业化的考量所稀释。</p>
<h2>对恐怖题材的革新</h2>
<p>《夜勤病栋》开创了一种全新的恐怖范式——将场景限定在一个封闭空间内（医院），通过时间维度（深夜）来创造恐怖感。这种"空间恐怖"的创作手法在此后被大量模仿，但很少有作品能够达到它的高度。</p>
<p>它还重新定义了"恐怖"与"温情"的关系。故事最终的结局并非纯粹的绝望，而是包含了救赎与和解的元素，这让它区别于那些单纯追求惊吓效果的恐怖作品。</p>
<h2>跨媒介影响</h2>
<p>作品的影响力远超其原始媒介。它启发了大量的小说、漫画、甚至电子游戏的创作。医院题材的恐怖游戏几乎都能看到《夜勤病栋》的影子。它在互联网社区中也催生了大量的二次创作、分析文章和讨论话题。</p>`,
    image: 'https://readdy.ai/api/search-image?query=Abstract%20artistic%20representation%20of%20cultural%20influence%20and%20media%20impact%2C%20dark%20hospital%20themed%20collage%20with%20film%20reels%2C%20books%2C%20and%20digital%20screens%2C%20glowing%20red%20and%20dark%20tones%2C%20conceptual%20art%2C%20atmospheric%2C%20layered%20composition%2C%20horror%20culture%20aesthetic&width=1600&height=900&seq=article-a5-hero&orientation=landscape',
    imageWidth: 1600,
    imageHeight: 900,
    date: '2026-07-28',
    author: '暗夜解析人',
    readTime: 9,
    featured: false,
    tags: ['夜勤病栋', '文化影响', '恐怖文化', '亚文化'],
  },
  {
    id: 'a6',
    slug: 'xinshou-guankan-zhinan',
    title: '夜勤病栋观看指南：新老观众的最佳体验方式',
    category: 'viewing-guide',
    excerpt: '无论你是第一次接触还是重温经典，这份观看指南将帮助你在最佳状态下体验夜勤病栋的魅力。',
    content: `<p>《夜勤病栋》是一部值得反复观看的作品。不同的观看方式和观看环境会带来截然不同的体验。以下是我们精心整理的观看指南。</p>
<h2>最佳观看时间</h2>
<p>毫无疑问，深夜是观看本作的最佳时段。建议在晚上十一点之后开始观看，这样当你沉浸其中时，现实世界的时钟也恰好指向故事中事件发生的时刻。这种时间上的同步会极大地增强沉浸感。</p>
<p>如果条件允许，选择在阴雨天的夜晚观看效果更佳。雨滴敲打窗户的声音与作品中医院的环境音形成了奇妙的呼应。</p>
<h2>最佳观看环境</h2>
<p>关闭所有灯光，只保留屏幕的光源。使用耳机而非外放——作品的音效设计是其恐怖感的核心来源之一，耳机能让你不错过任何一个细微的声音细节。</p>
<p>建议独自观看。虽然和朋友一起看可能减轻恐惧感，但独处的孤独感能让作品的影响力发挥到最大。如果容易受惊吓，身边放一杯温水和一条毯子会是不错的选择。</p>
<h2>观看顺序建议</h2>
<p>对于首次观看的观众，建议按照作品的自然顺序观看，不要跳集或预先查看剧透。在观看完每一部分后，给自己一些时间消化——作品中有大量的细节需要回味和思考。</p>`,
    image: 'https://readdy.ai/api/search-image?query=Dark%20room%20at%20night%20with%20a%20laptop%20screen%20glowing%2C%20headphones%20on%20desk%2C%20warm%20blanket%2C%20rain%20on%20window%2C%20cozy%20but%20eerie%20atmosphere%2C%20dim%20warm%20lamp%20in%20corner%2C%20cinematic%20lighting%2C%20moody%20ambient%2C%20watching%20horror%20vibe&width=1600&height=900&seq=article-a6-hero&orientation=landscape',
    imageWidth: 1600,
    imageHeight: 900,
    date: '2026-07-25',
    author: '暗夜解析人',
    readTime: 7,
    featured: false,
    tags: ['夜勤病栋', '观看指南', '观影建议'],
  },
  {
    id: 'a7',
    slug: 'yincang-xijie-jiedu',
    title: '夜勤病栋中的隐藏细节解读：你可能错过的关键线索',
    category: 'plot-analysis',
    excerpt: '揭秘作品中那些容易被忽略但至关重要的隐藏细节，每一个都是解开谜题的关键线索。',
    content: `<p>《夜勤病栋》是一部需要反复观看才能完全理解的作品。导演在画面中埋藏了大量的视觉线索和隐喻，以下是一些最容易被忽略但极为关键的隐藏细节。</p>
<h2>背景中的日历</h2>
<p>在多个场景中，护士站的背景墙上都挂着一本日历。细心的观众会发现，无论剧情时间如何推进，这本日历始终停留在三十年前的同一天——那位小患者去世的日子。这个细节直到最终夜才被直接点明，但导演从第一夜开始就在暗示这个关键信息。</p>
<h2>病房号码的秘密</h2>
<p>作品中反复出现的几个病房号码并非随意选择。304、217、138——这些数字如果按照医院当年的病房分配图来看，恰好形成了一个三角形，而三角形的中心正是当年发生医疗事故的手术室位置。这种空间隐喻是导演精心设计的叙事元素。</p>
<h2>药瓶标签</h2>
<p>在多个特写镜头中，可以清楚地看到护士使用的药瓶上的标签。放大观察会发现，某些药瓶上的日期已经过期了三十年。这不是道具组的失误，而是导演在暗示——整个医院的时间感已经发生了扭曲。</p>`,
    image: 'https://readdy.ai/api/search-image?query=Close-up%20of%20an%20old%20dusty%20calendar%20on%20a%20hospital%20wall%2C%20dim%20lighting%2C%20vintage%20medical%20aesthetic%2C%20eerie%20mood%2C%20dark%20shadows%2C%20macro%20photography%20style%2C%20aged%20paper%20texture%2C%20mysterious%20atmosphere%2C%20cinematic%20horror&width=1600&height=900&seq=article-a7-hero&orientation=landscape',
    imageWidth: 1600,
    imageHeight: 900,
    date: '2026-07-20',
    author: '暗夜解析人',
    readTime: 11,
    featured: false,
    tags: ['夜勤病栋', '隐藏细节', '解读', '彩蛋'],
  },
  {
    id: 'a8',
    slug: 'xinli-kongbu-jizhi',
    title: '心理恐怖机制研究：夜勤病栋如何操控你的情绪',
    category: 'horror-elements',
    excerpt: '深入探讨夜勤病栋所运用的心理恐怖机制，从认知科学角度解析作品如何精准操控观众的情绪反应。',
    content: `<p>恐怖作品的核心不在于展示多少血腥画面，而在于如何操控观众的心理状态。《夜勤病栋》在这方面的成就堪称典范。</p>
<h2>预期焦虑的建立</h2>
<p>作品大量使用了"预期焦虑"技巧——让观众预感到恐怖即将发生，却不立刻揭示恐怖的内容。这种悬而未决的状态比恐怖本身更加令人不安。导演通过缓慢的镜头推进、逐渐升高的背景音、以及角色表情的微妙变化来建立这种预期。</p>
<h2>安全空间的瓦解</h2>
<p>医院本应是安全的场所，但作品将其转化为恐怖的源头。这种对安全空间的颠覆让观众产生了深层的不安——如果连医院都不安全，那还有哪里可以信任？</p>
<h2>共情机制</h2>
<p>作品的成功很大程度上归功于观众对主角美咲的强烈共情。我们不仅仅是在观看她的经历，而是仿佛亲身经历着她的恐惧。这种共情让每一个恐怖场景的冲击力倍增。</p>`,
    image: 'https://readdy.ai/api/search-image?query=Abstract%20representation%20of%20psychological%20fear%20and%20anxiety%2C%20dark%20wavy%20shapes%2C%20human%20brain%20silhouette%20with%20red%20glowing%20center%2C%20dark%20atmospheric%2C%20conceptual%20art%2C%20surreal%2C%20emotional%20turmoil%2C%20no%20gore%2C%20artistic%20dark%20aesthetic&width=1600&height=900&seq=article-a8-hero&orientation=landscape',
    imageWidth: 1600,
    imageHeight: 900,
    date: '2026-07-18',
    author: '暗夜解析人',
    readTime: 10,
    featured: false,
    tags: ['夜勤病栋', '心理恐怖', '情绪研究'],
  },
];

export const getArticlesByCategory = (categoryId: string): Article[] => {
  return articles.filter((a) => a.category === categoryId);
};

export const getFeaturedArticles = (): Article[] => {
  return articles.filter((a) => a.featured);
};

export const getArticleBySlug = (slug: string): Article | undefined => {
  return articles.find((a) => a.slug === slug);
};

export const getCategoryById = (id: string) => {
  return categories.find((c) => c.id === id);
};